import { type FC } from "react";

import { Text } from "@/components/flexkit/typography/Text";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { Flags } from "@/feature-flags";
import { useFlag, useFlagOnaAIEnabled, useFlagSuperOnaTerminalEnabled } from "@/hooks/use-flag";
import { TrackLocations } from "@/hooks/use-segment";
import {
    CommandPaletteKeyBinding,
    CreateEnvironmentModalKeyBinding,
    CreateTerminalKeyBinding,
    CycleAgentModeKeyBinding,
    FocusConversationPromptKeyBinding,
    FocusTerminalKeyBinding,
    KeyboardHelpKeyBinding,
    OpenInEditorKeyBinding,
    OpenSettingsPageKeyBinding,
    ToggleAISidebarKeyBinding,
    ToggleDetailsPanelKeyBinding,
    ToggleActivityPanelKeyBinding,
    ToggleSidebarKeyBinding,
} from "@/utils/platform";

type KeyboardShortcut = {
    keys: string[];
    description: string;
    category: "general" | "navigation" | "ai" | "terminal";
};
const shortcuts: KeyboardShortcut[] = [
    // General shortcuts
    {
        keys: CommandPaletteKeyBinding,
        description: "Command palette",
        category: "general",
    },
    {
        keys: CreateEnvironmentModalKeyBinding,
        description: "New environment",
        category: "general",
    },
    {
        keys: OpenInEditorKeyBinding,
        description: "Open environment in preferred editor",
        category: "general",
    },
    {
        keys: KeyboardHelpKeyBinding,
        description: "Show keyboard shortcuts (this dialog)",
        category: "general",
    },
    {
        keys: OpenSettingsPageKeyBinding,
        description: "Go to settings",
        category: "general",
    },
    // Navigation shortcuts
    {
        keys: ToggleSidebarKeyBinding,
        description: "Toggle main sidebar",
        category: "navigation",
    },
    {
        keys: ToggleDetailsPanelKeyBinding,
        description: "Toggle environment details sidebar",
        category: "navigation",
    },
    {
        keys: ToggleAISidebarKeyBinding,
        description: "Toggle AI sidebar",
        category: "ai",
    },
    {
        keys: FocusConversationPromptKeyBinding,
        description: "Focus conversation prompt",
        category: "ai",
    },
    {
        keys: OpenInEditorKeyBinding,
        description: "Toggle VS Code Browser sidebar",
        category: "ai",
    },
    {
        keys: CycleAgentModeKeyBinding,
        description: "Cycle prompt mode",
        category: "ai",
    },
    // Terminal shortcuts
    {
        keys: FocusTerminalKeyBinding,
        description: "Focus terminal",
        category: "terminal",
    },
    {
        keys: CreateTerminalKeyBinding,
        description: "Create new terminal",
        category: "terminal",
    },
];

type KeyboardShortcutsModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const KeyboardShortcutsModal: FC<KeyboardShortcutsModalProps> = ({ open, onOpenChange }) => {
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { value: isNotificationsEnabled } = useFlag(Flags.NotificationsEnabled, false);
    const { value: isTerminalEnabled } = useFlagSuperOnaTerminalEnabled();

    const generalShortcuts = shortcuts.filter((s) => s.category === "general");
    const navigationShortcuts = shortcuts.filter((s) => s.category === "navigation");
    const aiShortcuts = isOnaEnabled ? shortcuts.filter((s) => s.category === "ai") : [];
    const terminalShortcuts = isTerminalEnabled ? shortcuts.filter((s) => s.category === "terminal") : [];

    if (isNotificationsEnabled) {
        navigationShortcuts.push({
            keys: ToggleActivityPanelKeyBinding,
            description: "Toggle activity panel",
            category: "navigation",
        });
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-track-location={TrackLocations.KeyboardShortcutsModal}>
                <DialogHeader>
                    <DialogTitle>Keyboard Shortcuts</DialogTitle>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-6">
                    <ShortcutSection title="General" shortcuts={generalShortcuts} />
                    <ShortcutSection title="Navigation" shortcuts={navigationShortcuts} />
                    <ShortcutSection title="Ona sessions" shortcuts={aiShortcuts} />
                    <ShortcutSection title="Terminal" shortcuts={terminalShortcuts} />
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};

type ShortcutSectionProps = {
    title: string;
    shortcuts: KeyboardShortcut[];
};

const ShortcutSection: FC<ShortcutSectionProps> = ({ title, shortcuts }) => {
    if (shortcuts.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            <Text className="text-sm font-semibold text-content-secondary">{title}</Text>
            <div className="flex flex-col gap-1">
                {shortcuts.map((shortcut, index) => (
                    <div key={index} className="flex items-center justify-between py-1.5">
                        <Text className="text-sm">{shortcut.description}</Text>
                        <KeyboardCombo keys={shortcut.keys} className="bg-surface-02" />
                    </div>
                ))}
            </div>
        </div>
    );
};
